import { axiosInstanceWhatsAppConfirmation, getLocalOwnerIdOverride } from "../instance";
import { decodeTokenService } from "../decodeTokenService";

export type SupportStatus = "draft" | "active" | "paused";
export type ConversationStatus = "open" | "resolved";
export type ConversationPriority = "normal" | "medium" | "critical";
export type TemplateStatus =
  | "APPROVED"
  | "PENDING"
  | "REJECTED"
  | "PAUSED"
  | "DISABLED"
  | "IN_APPEAL"
  | "PENDING_DELETION"
  | "DELETED"
  | "DRAFT"
  | "UNKNOWN";

export interface SupportSettings {
  exists: boolean;
  id?: number;
  owner_id?: string | null;
  status: SupportStatus | string;
  business_name: string | null;
  bot_name: string | null;
  custom_instructions: string | null;
  ai_enabled: boolean;
  voice_replies_enabled: boolean;
  learning_mode_enabled: boolean;
  humanized_replies_enabled: boolean;
  order_confirmations_enabled: boolean;
  order_confirmation_product_scope: string;
  order_confirmation_selected_products: string[];
  order_state_confirmed_instructions: string;
  order_state_rejected_instructions: string;
  order_state_auto_expire_enabled: boolean;
  order_state_auto_expire_hours: number;
  customer_state_purchased_product_scope: string;
  customer_state_purchased_products: string[];
  customer_state_support_active_instructions: string;
  customer_state_support_resolved_instructions: string;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export type SupportSettingsPayload = Partial<
  Pick<
    SupportSettings,
    | "status"
    | "business_name"
    | "bot_name"
    | "custom_instructions"
    | "ai_enabled"
    | "voice_replies_enabled"
    | "learning_mode_enabled"
    | "humanized_replies_enabled"
    | "order_confirmations_enabled"
    | "order_confirmation_product_scope"
    | "order_confirmation_selected_products"
    | "order_state_confirmed_instructions"
    | "order_state_rejected_instructions"
    | "order_state_auto_expire_enabled"
    | "order_state_auto_expire_hours"
    | "customer_state_purchased_product_scope"
    | "customer_state_purchased_products"
    | "customer_state_support_active_instructions"
    | "customer_state_support_resolved_instructions"
    | "metadata_json"
  >
>;

export interface SupportAutomation {
  id: number;
  owner_id: string;
  automation_key: string;
  title: string | null;
  enabled: boolean;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  product_scope: string;
  product_ids: string[];
  action_type: string;
  action_config: Record<string, unknown>;
  followup_enabled: boolean;
  followup_delay_seconds: number | null;
  followup_action_type: string | null;
  followup_action_config: Record<string, unknown>;
  configured: boolean;
  requested_enabled: boolean;
  frontend_payload: Record<string, unknown>;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  // Salud visible por accion: fallos de envio recientes (7 dias) y ultimo motivo.
  recent_failures?: {
    count: number;
    last_error: string | null;
    last_failed_at: string | null;
  };
}

export interface SupportAutomationRun {
  id: number;
  owner_id: string;
  automation_id: number;
  automation_key: string;
  event_type: string;
  event_id: string;
  phase: string;
  trigger_type: string;
  action_type: string;
  status: string;
  scheduled_for: string | null;
  trigger_payload: Record<string, unknown>;
  action_config: Record<string, unknown>;
  result_json: Record<string, unknown>;
  error_message_sanitized: string | null;
  metadata_json: Record<string, unknown>;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SupportContact {
  id: number;
  owner_id: string;
  channel: string;
  external_id: string | null;
  phone: string | null;
  name: string | null;
  email: string | null;
  alias: string | null;
  tags: string[];
  notes: unknown[];
  custom_attributes: Record<string, unknown>;
  opted_out_at: string | null;
  last_seen_at: string | null;
}

export interface PersonChannelIdentity {
  id: number;
  channel: string;
  external_id: string | null;
  phone: string | null;
  verified: boolean;
  last_seen_at: string | null;
}

export interface PersonInfo {
  id: number;
  display_name: string | null;
  email: string | null;
  tags: string[];
  notes: unknown[];
  custom_attributes: Record<string, unknown>;
  lifecycle_state: string;
  opted_out: boolean;
  primary_phone: string | null;
  created_at: string | null;
}

export interface PersonView {
  person_id: number | null;
  person: PersonInfo | null;
  primary_contact: SupportContact | null;
  identities: PersonChannelIdentity[];
  contacts: SupportContact[];
  conversations: SupportConversation[];
  orders: SupportConfirmation[];
}

// Resultado de separar un contacto de su persona unificada (unmerge).
export interface ContactUnmergeResult {
  status: string;
  old_person_id: number | null;
  new_person_id: number | null;
  moved: Record<string, unknown> | null;
  resultado?: string | null;
}

export interface SupportContactNote {
  note: string;
  category: string;
  source: string;
  conversation_id: number | null;
  wa_message_id: string | null;
  private?: boolean;
  created_at: string;
}

export interface SupportContactUpdate {
  name?: string | null;
  email?: string | null;
  alias?: string | null;
  phone?: string | null;
  tags?: string[];
  custom_attributes?: Record<string, unknown>;
}

export interface SupportConversation {
  id: number;
  owner_id: string;
  contact_id: number | null;
  contact: SupportContact | null;
  channel: string;
  status: ConversationStatus | string;
  source: string | null;
  ai_enabled: boolean;
  assignee_id: string | null;
  assignee_name: string | null;
  priority: ConversationPriority | string;
  labels: string[];
  human_help_requested?: boolean;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_customer_message_at: string | null;
  last_bot_message_at: string | null;
  last_human_message_at: string | null;
  unread_count: number;
  resolved_at: string | null;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
  session_window?: {
    open: boolean | null;
    expires_at: string | null;
    reason?: "active" | "expired" | "awaiting_customer_reply" | null;
  } | null;
}

export interface SupportConversationPage {
  items: SupportConversation[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface SupportConversationUpdate {
  status?: ConversationStatus | string;
  ai_enabled?: boolean;
  assignee_id?: string | null;
  assignee_name?: string | null;
  priority?: ConversationPriority | string;
  labels?: string[];
  human_help_requested?: boolean;
}

export interface SupportMessage {
  id: number;
  owner_id: string;
  conversation_id: number;
  contact_id: number | null;
  sender_type: string;
  sender_id: string | null;
  sender_name: string | null;
  direction: "inbound" | "outbound" | string;
  channel: string;
  content: string;
  content_type: string;
  private: boolean;
  turn_id: string | null;
  wa_message_id: string | null;
  template_name: string | null;
  delivery_status: string | null;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
}

export interface ChatbotEvent {
  id: number;
  owner_id: string;
  event_type: string;
  actor_type: string | null;
  actor_id: string | null;
  conversation_id: number | null;
  contact_id: number | null;
  wa_message_id: string | null;
  severity: string;
  details: Record<string, unknown>;
  trace_id: string | null;
  created_at: string | null;
}

export interface ChatbotDeliveryLog {
  id: number;
  owner_id: string;
  conversation_id: number | null;
  message_id: number | null;
  wa_message_id: string | null;
  direction: string;
  channel: string;
  message_type: string | null;
  template_name: string | null;
  status: string;
  meta_error_code: string | null;
  meta_error_subcode: string | null;
  meta_error_message_sanitized: string | null;
  error_family?: string | null;
  error_code?: string | null;
  short_label?: string | null;
  owner_message?: string | null;
  technical_message_sanitized?: string | null;
  retryable?: boolean | null;
  recommended_action?: string | null;
  provider_payload_hash: string | null;
  trace_id?: string | null;
  created_at: string | null;
  updated_at: string | null;
  recovered_at?: string | null;
  recovery_message_id?: number | null;
  recovery_kind?: "message" | "template" | null;
}

export interface SupportAIGenerationAttempt {
  id: number;
  owner_id: string;
  conversation_id: number;
  contact_id: number | null;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  input_last_message_id: number | null;
  input_last_customer_message_at: string | null;
  input_last_human_message_at: string | null;
  trigger_wa_message_ids: string[];
  model_provider: string | null;
  model_name: string | null;
  fallback_level: string | null;
  trace_id: string | null;
  error_family: string | null;
  error_code: string | null;
  error_message_sanitized: string | null;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface SupportNotification {
  id: number;
  owner_id: string;
  notification_key: string;
  type: string;
  title: string;
  body: string | null;
  status: "unread" | "read" | string;
  target_type: string | null;
  target_id: string | null;
  target_payload: Record<string, unknown>;
  source_event_id: number | null;
  source_trace_id: string | null;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  read_at: string | null;
  updated_at: string | null;
}

// Conteos por estado REAL (no por notificaciones sin leer): sugerencias pendientes,
// conversaciones con ayuda humana activa y conversaciones sin responder.
export interface NotificationsSummary {
  suggestions_pending: number;
  human_help_active: number;
  unanswered: number;
  pending_approvals?: number;
  delivery_error?: number;
}

export interface ReplyApprovalActions {
  executed: string[];
  held: string[];
  consults: { label: string; tool_name: string }[];
  /** Diagnóstico: nombres técnicos de tools sin frase en el catálogo. NO se pinta al dueño. */
  unmapped: string[];
}

export interface ReplyApprovalVersion {
  version: number;
  text: string;
  parts: string[];
  voice_reply_expected: boolean;
  /**
   * La nota de voz ya está sintetizada desde el gate (no "al aprobar"): si `available`,
   * el audio se pre-escucha ANTES de aprobar vía getReplyApprovalVersionVoice → URL firmada.
   */
  voice_media: { available: boolean } | null;
  voice_synthesis_failed: boolean;
  feedback_text: string | null;
  superseded_by_customer: boolean;
  created_at: string | null;
  actions: ReplyApprovalActions;
}

export interface ReplyApproval {
  id: number;
  status: "pending" | "approving" | "approved" | "rejected" | "expired" | "send_failed";
  channel: string;
  conversation_id: number;
  approved_version: number | null;
  reject_reason: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  feedback_history: { version_produced: number; text: string; created_at: string | null }[];
  versions: ReplyApprovalVersion[];
  /**
   * Frases de las acciones que la IA intentará deshacer si se rechaza. Es lo ÚNICO que se puede
   * prometer al rechazar: el backend la calcula desde la misma fuente que arma el turno de reversa
   * y cubre todas las versiones. No usar `actions.executed` para esto (trae tools no reversibles
   * y solo de una versión).
   */
  reversible: string[];
  /** "window_closed" (expiró la ventana 24h) o "learning_mode_disabled" (el owner apagó el modo — sin letrero). */
  discard_reason?: string | null;
  window: { open: boolean | null; expires_at: string | null };
}

export interface ChatbotRealtimeEvent {
  version: number;
  type: string;
  owner_id: string;
  conversation_id: number | null;
  message_id: number | null;
  wa_message_id: string | null;
  trace_id: string | null;
  created_at: string | null;
}

export interface WhatsAppInboundEvent {
  id: number;
  owner_id: string;
  phone_number_id: string | null;
  waba_id: string | null;
  wa_message_id: string | null;
  sender_phone_suffix: string | null;
  sender_phone_hash: string | null;
  event_kind: string | null;
  message_type: string | null;
  trace_id: string | null;
  payload_sha256: string | null;
  status: string;
  route_target: string | null;
  attempt_count: number;
  error: string | null;
  last_error: string | null;
  received_at: string | null;
  processed_at: string | null;
}

export interface WhatsAppConnectionStatus {
  exists: boolean;
  owner_id: string;
  status: string;
  phone_number_id: string | null;
  waba_id: string | null;
  display_name: string | null;
  is_active: boolean;
  access_token_configured: boolean;
  app_secret_configured: boolean;
  credentials_encrypted: boolean;
  credentials_readable?: boolean;
  credential_error?: string | null;
  verify_token: string | null;
  webhook_url: string;
  owner_webhook_url: string;
  recommended_webhook_url: string;
  subscribe_fields: string[];
  setup_mode: "manual_cloud_api" | string;
  health?: {
    token_status: string | null;
    token_expires_at: string | null;
    quality_rating: string | null;
    messaging_limit_tier: string | null;
    last_health_check_at: string | null;
  };
  default_templates?: DefaultTemplatesPayload;
}

export interface ManualWhatsAppAccountPayload {
  phone_number_id?: string;
  waba_id?: string;
  access_token?: string;
  app_secret?: string;
  display_name?: string;
  is_active?: boolean;
  rotate_verify_token?: boolean;
}

export interface WhatsAppTemplate {
  id: number;
  name: string;
  language: string;
  status: TemplateStatus | string;
  category: string;
  // Rol funcional de la plantilla dentro del chatbot (confirmation_flow, reminder,
  // dropi_*, guardian_*, etc.). null para plantillas custom del owner.
  purpose?: string | null;
  components: unknown[] | null;
  body_text: string | null;
  variable_count: number;
  rejection_reason: string | null;
  quality_rating: string | null;
  quality_score: string | null;
  meta_template_id: string | null;
  last_synced_at: string | null;
  created_at: string | null;
}

export interface DefaultTemplatesPayload {
  confirmation_template_name?: string | null;
  reminder_template_name?: string | null;
  fallback_template_name?: string | null;
  cart_recovery_template_name?: string | null;
  order_confirmed_template_name?: string | null;
}

export interface LearningEntry {
  id?: string;
  date?: string;
  suggestion_uid?: string | null;
  suggestion_number?: number | null;
  text: string;
  example_summary?: string | null;
  category?: string | null;
}

export interface KnowledgeSource {
  id: number;
  owner_id: string;
  name: string;
  category: string | null;
  content: string;
  source_type: string;
  is_active: boolean;
  char_count: number;
  metadata_json: Record<string, unknown>;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Fuentes tipo learning_log (base "Sugerencias aprobadas") exponen las cajitas top-level.
  entries?: LearningEntry[];
}

export interface GuardianSettings {
  exists: boolean;
  id?: number;
  owner_id?: string | null;
  enabled?: boolean;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface GuardianSettingsRequest {
  enabled?: boolean;
  metadata_json: {
    suggestion_instructions?: string;
    review_interval_minutes?: number;
    review_prompt?: string;
    summary_interval_hours?: number;
    summary_prompt?: string;
  };
}

export interface KnowledgeDocumentExtractionResult {
  text: string;
  page_count?: number | null;
  warnings?: string[];
  extraction_status?: string;
}

export interface GuardianRun {
  id: number;
  owner_id: string;
  run_type: string;
  status: string;
  summary: string | null;
  error: string | null;
  trace_id: string | null;
  metadata_json: Record<string, unknown>;
  started_at: string | null;
  finished_at: string | null;
}

export interface GuardianReview {
  id: number;
  owner_id: string;
  run_id: number | null;
  status: string;
  summary: string | null;
  findings: unknown[];
  metadata_json: Record<string, unknown>;
  created_at: string | null;
}

export interface GuardianReport {
  id: number;
  owner_id: string;
  run_id: number | null;
  status: string;
  summary: string | null;
  review_ids: unknown[];
  metadata_json: Record<string, unknown>;
  created_at: string | null;
}

export interface GuardianMemoryItem {
  id: string;
  title: string | null;
  content: string | null;
  status: string;
  tags: string[];
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface GuardianTimelineItem {
  kind: "review" | "report" | "daily";
  id: string | number;
  summary: string | null;
  created_at?: string | null;
  started_at?: string | null;
  findings_count?: number;
}

export interface GuardianProposal {
  id: string;
  number: number | null;
  suggestion_number: number | null;
  kind: string | null;
  title: string | null;
  description: string | null;
  status: string;
  impact: string | null;
  metadata_json: Record<string, unknown>;
  created_by: string | null;
  created_at: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
}

export interface GuardianChatMessage {
  id: string;
  conversation_id: string;
  sender: "guardian" | "owner";
  sender_type: string;
  content: string;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
}

export interface GuardianConversation {
  id: string;
  title: string | null;
  preview: string | null;
  status: string;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
  messages: GuardianChatMessage[];
}

export interface GuardianState {
  owner_id: string;
  settings: GuardianSettings;
  state: Record<string, unknown>;
  memory_count: number;
  active_memory_count: number;
  proposal_count: number;
  open_proposal_count: number;
  recent_runs: GuardianRun[];
  recent_reviews: GuardianReview[];
  recent_reports: GuardianReport[];
}

export interface SupportTestChatResponse {
  text: string;
  action: string | null;
  action_params: Record<string, unknown>;
  tool_name: string | null;
  tools_available: boolean;
  degraded_mode: boolean;
  usage: {
    provider: string;
    model: string;
    input_tokens: number;
    output_tokens: number;
  } | null;
}

export interface SupportTestChatHistoryItem {
  sender: "customer" | "assistant" | "owner";
  content: string;
}

export interface SupportConfirmation {
  id: number;
  order_id: number | null;
  display_code: string | null;
  order_hash_id: string | null;
  owner_id: string;
  customer_name: string | null;
  customer_phone: string | null;
  status: string;
  raw_status: string;
  state_bucket?: string;
  state_label?: string;
  source?: "chat_confirmation" | "ecommerce" | "both";
  // Estado de la fila ecommerce del mismo pedido (dedupe): permite que el chip
  // único progrese a estados logísticos (Entregada/Cancelada) sin otra llamada.
  ecommerce_status?: string | null;
  delivery_issue?: boolean;
  delivery_issue_label?: string;
  tags: string[];
  total?: number | string | null;
  product_summary?: string | null;
  products?: Array<{ name: string; quantity: number }>;
  retry_count: number;
  next_retry_at: string | null;
  confirmed_at: string | null;
  expired_at: string | null;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface MetricsTopProduct {
  product_id: string;
  name: string;
  image_url?: string | null;
  units: number;
}

export interface SupportOverview {
  since: string;
  until?: string | null;
  conversations: {
    total: number;
    active: number;
    by_status: Record<string, number>;
  };
  period?: {
    conversations_total: number;
    conversations_processed: number;
  };
  chat_orders?: {
    total: number;
    by_payment: {
      cod: number;
      prepaid: number;
    };
    top_products: MetricsTopProduct[];
  };
  confirmations_period?: {
    confirmed: number;
    rejected: number;
    top_products: MetricsTopProduct[];
  };
  contacts: { total: number };
  messages: {
    inbound: number;
    outbound: number;
    by_direction: Record<string, number>;
  };
  delivery: {
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    by_status: Record<string, number>;
  };
  events: { by_type: Record<string, number> };
  guardian: { runs_by_status: Record<string, number> };
}

export interface OperationsHealth {
  owner_id: string;
  status: "ok" | "warning" | "critical" | string;
  window: {
    since: string;
    now: string;
    default_hours: number;
    stale_inbound_minutes: number;
  };
  alerts: {
    code: string;
    severity: "info" | "warning" | "critical" | string;
    message: string;
    count?: number;
  }[];
  whatsapp: Record<string, unknown>;
  templates: {
    total: number;
    approved: number;
    by_status: Record<string, number>;
  };
  support: {
    settings_exists: boolean;
    active: boolean;
    ai_enabled: boolean;
    conversations: {
      total: number;
      active: number;
      waiting_for_human: number;
      by_status: Record<string, number>;
      active_by_priority: Record<string, number>;
    };
  };
  confirmations: {
    ai_config_exists: boolean;
    ai_enabled_default: boolean;
    total: number;
    by_status: Record<string, number>;
    send_failed: number;
  };
  inbound: Record<string, unknown>;
  delivery: Record<string, unknown>;
  guardian: Record<string, unknown>;
  events: Record<string, unknown>;
}

export interface ConfirmationsOverview {
  owner_id: string;
  total: number;
  actionable: number;
  send_failed: number;
  by_status: Record<string, number>;
  ai_config: Record<string, unknown>;
}

export type CommerceFlowType = "confirmation" | "order_capture";
export type CommerceOrderMode = "chat_capture_cod" | "fluxi_checkout_link";

// Imagen candidata que devuelve ecommerce en GET /websites/{id}/chatbot-images.
export type FunnelChatbotImageCandidate = {
  key: string;
  url: string;
  source: "portada" | "galeria_producto" | "pagina" | string;
  description?: string | null;
  sectionName?: string | null;
};

export type FunnelChatbotImagesResponse = {
  website_id: string;
  images: FunnelChatbotImageCandidate[];
};

// Imagen elegida para que la IA la envíe en el chat (ventas).
export type CommerceChatbotSelectedImage = {
  key: string;
  url: string;
  description: string;
  source: string;
};

export type CommerceChatbotImagesConfig = {
  selected: CommerceChatbotSelectedImage[];
  instructions: string;
};

// Imagen única para la plantilla de confirmación con foto.
export type CommerceConfirmationImage = {
  key: string;
  url: string;
  description: string;
};

export type CommerceCheckoutProductMapping = {
  product_source?: string;
  product_ref: string;
  product_key?: string;
  website_id: string;
  checkout_url?: string;
  checkout_url_snapshot?: string;
  description_snapshot?: string;
  funnel_number?: number | string;
  website_name?: string;
  product_name?: string;
  sales_angle?: string;
  sales_angle_description?: string;
  sales_angle_complementary_angles?: string[];
  payment_context?: Record<string, unknown>;
  fulfillment_context?: Record<string, unknown>;
  chatbot_images?: CommerceChatbotImagesConfig;
  confirmation_image?: CommerceConfirmationImage;
  // Ofertas por cantidad del embudo apagadas para el chat (units 1/2/3...).
  disabled_offer_units?: number[];
};

export type CommerceLogisticsPolicy = {
  productScope?: "all" | "selected";
  dropiConfirmationEnabled?: boolean;
  dropiGuideNotifyEnabled?: boolean;
  dropiGuideTemplateName?: string;
  dropiGuideInstructions?: string;
  smallMunicipalityOffice?: boolean;
  smallMunicipalityOfficeMode?: "suggest" | "require_acceptance";
  smallMunicipalityOfficeInstructions?: string;
  askPreferredCarrier?: boolean;
  preferredCarrierMode?: "ask_customer" | "rules";
  preferredCarrierRules?: string;
  shippingSurchargeEnabled?: boolean;
  shippingSurchargeThreshold?: number;
  shippingSurchargePercent?: number;
  shippingSurchargeMode?: "percent" | "fixed";
  shippingSurchargeFixedAmount?: number;
  mapsValidationEnabled?: boolean;
  mapsRejectOrderIfInvalid?: boolean;
  autoExpireEnabled?: boolean;
  autoExpireHours?: number;
  confirmedInstructions?: string;
  rejectedInstructions?: string;
  // M9: flags de APLICACIÓN de las instrucciones personalizadas. OFF = el texto se
  // conserva como borrador pero no se aplica. En el payload viajan como snake_case
  // (confirmed_instructions_enabled / rejected_instructions_enabled) dentro del mismo
  // logistics_policy_json; aquí se representan camelCase para el draft interno.
  confirmedInstructionsEnabled?: boolean;
  rejectedInstructionsEnabled?: boolean;
};

export type CommerceFlowDraft = {
  id: string;
  title: string;
  enabled: boolean;
  flowType: CommerceFlowType;
  mode: CommerceOrderMode | null;
  templateName: string;
  templateLanguage: string;
  instructions: string;
  productScope: "all" | "selected";
  productIds: string[];
  productLabels?: Record<string, string>;
  productImages?: Record<string, string>;
  productDescriptions?: Record<string, string>;
  requiredFields: string[];
  checkoutWebsiteIds: string[];
  checkoutWebsiteByProductId?: Record<string, string>;
  // Ventas: imágenes del embudo elegidas por producto para que la IA las envíe en el chat.
  chatbotImagesByProductId?: Record<string, CommerceChatbotImagesConfig>;
  // Ventas: units de ofertas por cantidad del embudo apagados para el chat, por producto.
  disabledOfferUnitsByProductId?: Record<string, number[]>;
  // Confirmación: foto única por producto para la plantilla de confirmación con foto.
  confirmationImageByProductId?: Record<string, CommerceConfirmationImage>;
  confirmationPhotosEnabled?: boolean;
  logisticsPolicy: CommerceLogisticsPolicy;
  ephemeral?: boolean;
};

export type CommerceFlowProduct = {
  product_source: string;
  product_ref: string;
  label_snapshot?: string | null;
  image_snapshot?: string | null;
};

export type CommerceFlowApiRow = {
  id: string;
  flow_key?: string;
  flow_type: CommerceFlowType | string;
  name?: string;
  enabled?: boolean;
  mode?: CommerceOrderMode | string | null;
  template_name?: string | null;
  template_language?: string | null;
  instructions?: string | null;
  required_fields?: string[];
  checkout_config?: {
    website_ids?: Array<string | number>;
    product_mappings?: CommerceCheckoutProductMapping[];
    productMappings?: CommerceCheckoutProductMapping[];
    confirmation_photos_enabled?: boolean;
  };
  logistics_policy?: CommerceLogisticsPolicy;
  products?: CommerceFlowProduct[];
};

// Resultado del sync de alcance de confirmaciones al guardar la tab (nunca no-op
// silencioso): owner-flag "todos" en ecommerce o embudos habilitados por producto.
export type ConfirmationSyncResult = {
  mode: "all_products" | "selected_products" | "none" | "error" | string;
  owner_all_enabled?: boolean;
  owner_all_write_ok?: boolean;
  websites_enabled?: string[];
  products_requiring_confirmation?: number;
};

export type CommerceFlowsSaveResult = {
  flows: CommerceFlowApiRow[];
  confirmation_sync: ConfirmationSyncResult | null;
};

export type CommerceWebsiteSummary = {
  id: number | string;
  name?: string | null;
  brand_name?: string | null;
  url?: string | null;
  public_url?: string | null;
  primary_public_url?: string | null;
  primary_host?: string | null;
  legacy_onlinesl_url?: string | null;
  candidate_urls?: string[];
  product?: { name?: string | null; image?: string | null } | null;
  id_sequence_owner_id?: number | null;
};

// Contexto que el chatbot recibe de un producto dentro de un flujo de toma de órdenes
// (descripción, oferta del embudo, imágenes elegidas y el texto exacto del catálogo).
export interface CommerceFlowProductContextImage {
  key?: string | null;
  description?: string | null;
  [extra: string]: unknown;
}

// Oferta por cantidad del embudo (1/2/3 unidades) con su precio derivado y si
// está habilitada para ofrecerse en el chat.
export interface CommerceFlowQuantityOffer {
  units: number;
  precio: number | null;
  moneda: string | null;
  titulo: string;
  promocion: string | null;
  habilitada: boolean;
}

// Ángulo de venta del embudo (titulo + descripcion). El backend lo está agregando al
// product-context: el front debe tolerar que aún no venga.
export interface CommerceFlowProductAngle {
  titulo?: string | null;
  descripcion?: string | null;
}

export interface CommerceFlowProductContext {
  flow_id: string;
  product_id: string;
  producto: string | null;
  modo: string | null;
  descripcion: string | null;
  oferta: string | null;
  oferta_raw?: unknown;
  ofertas_cantidad: CommerceFlowQuantityOffer[];
  // units apagados para el chat en esta acción.
  ofertas_deshabilitadas: number[];
  imagenes_embudo: CommerceFlowProductContextImage[];
  instrucciones_imagenes: string | null;
  website_id: string | null;
  contexto_exacto_chatbot: string[];
  // ——— Campos nuevos del product-context (backend en despliegue paralelo): opcionales/tolerantes ———
  angulo_principal?: CommerceFlowProductAngle | null;
  angulos_secundarios?: Array<CommerceFlowProductAngle | string> | null;
  descripcion_fuente?: string | null;
  preview_image_url?: string | null;
  descripcion_adicional?: string | null;
  descripcion_catalogo?: string | null;
}

export interface ContactStateItem {
  id: number;
  contact: SupportContact;
  state: string;
  state_label: string;
  tags: string[];
  confirmed_orders: number;
  active_support: boolean;
  resolved_support: boolean;
  latest_order_at: string | null;
  latest_conversation_id: number | null;
}

export interface StateColumn<T> {
  id: string;
  label: string;
  count: number;
  items: T[];
}

export interface StateBoard<T> {
  domain: "orders" | "contacts" | string;
  title: string;
  columns: StateColumn<T>[];
  rows: T[];
}

export interface RetentionPreview {
  owner_id: string;
  policy: Record<string, number>;
  cutoffs: Record<string, string>;
  eligible: Record<string, number>;
}

export interface RetentionRunResult {
  owner_id: string;
  policy: Record<string, number>;
  cutoffs: Record<string, string>;
  deleted: Record<string, number>;
}

export interface AIModel {
  provider: string;
  model: string;
  display_name: string;
  input_cost_usd_per_1k: number;
  output_cost_usd_per_1k: number;
  credit_multiplier: number;
  estimated_credits_per_message: number;
}

export interface InboundRetryResult {
  retried: boolean;
  sent: boolean;
  conversation_id: number;
  contact_id: number;
  inbound_event_id: number;
  status: string;
}

export interface IntegrationCapability {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  verified: boolean;
  sensitive: boolean;
  write: boolean;
}

export interface IntegrationTool {
  tool_name: string;
  display_name: string;
  description: string;
  ai_description?: string | null;
  input_schema: Record<string, unknown>;
  output_schema: Record<string, unknown>;
  capability_key?: string | null;
  default_policy: string;
  policy: string;
  enabled: boolean;
  is_sensitive: boolean;
  is_write: boolean;
  metadata_json: Record<string, unknown>;
}

export interface IntegrationProvider {
  provider_key: string;
  display_name: string;
  description: string;
  capabilities: IntegrationCapability[];
  tools: IntegrationTool[];
}

export interface IntegrationConnection {
  id: number;
  owner_id: string;
  provider_key: string;
  provider: IntegrationProvider;
  display_name: string | null;
  status: string;
  is_active: boolean;
  ready_for_tools?: boolean;
  diagnostic_status: string | null;
  last_diagnosed_at: string | null;
  last_synced_at: string | null;
  metadata_json: Record<string, unknown>;
  secret_status: Record<string, boolean>;
  created_at: string | null;
  updated_at: string | null;
}

export interface IntegrationToolExecution {
  id: number;
  owner_id: string;
  connection_id: number | null;
  provider_key: string;
  tool_name: string;
  policy: string;
  status: string;
  actor_type: string;
  conversation_id: number | null;
  contact_id: number | null;
  idempotency_key: string | null;
  input_hash: string | null;
  input_summary_json: Record<string, unknown>;
  parent_execution_id: number | null;
  approval_requested_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  output_summary_json: Record<string, unknown>;
  error_message_sanitized: string | null;
  trace_id: string | null;
  latency_ms: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
}

export interface HotmartProductMapping {
  id: number;
  owner_id: string;
  connection_id: number;
  product_id: string | null;
  ucode: string | null;
  product_name: string;
  aliases: string[];
  club_subdomain: string | null;
  club_config_id: number | null;
  delivery_type: string;
  hotmart_status: string | null;
  product_type: string;
  is_active: boolean;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface HotmartAccessCase {
  id: number;
  owner_id: string;
  connection_id: number;
  conversation_id: number | null;
  contact_id: number | null;
  customer_email: string;
  customer_email_hash: string;
  product_id: string | null;
  product_name: string | null;
  case_status: string;
  case_reason: string;
  recommended_action: string | null;
  resolution_note: string | null;
  diagnosis_json: Record<string, unknown>;
  evidence_json: Array<Record<string, unknown>>;
  assigned_to: string | null;
  notification_status: string;
  verification_status: string;
  last_verified_at: string | null;
  resolved_at: string | null;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface HotmartClubConfig {
  id: number;
  owner_id: string;
  connection_id: number;
  display_name: string;
  club_slug: string | null;
  public_url: string | null;
  api_subdomain: string | null;
  status: string;
  last_test_status: string | null;
  last_test_error: string | null;
  last_tested_at: string | null;
  product_mapping_ids: number[];
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface HotmartWebhookEvent {
  id: number;
  connection_id: number;
  hotmart_event_id: string;
  event_type: string;
  version: string | null;
  transaction: string | null;
  product_id: string | null;
  subscription_code: string | null;
  processing_status: string;
  processing_attempts: number;
  processing_error: string | null;
  next_retry_at: string | null;
  received_at: string | null;
  processed_at: string | null;
}

export interface HotmartCommercialSignal {
  id: number;
  connection_id: number;
  webhook_event_id: number;
  signal_type: string;
  contact_id: number | null;
  conversation_id: number | null;
  product_id: string | null;
  status: string;
  consent_status: string;
  eligibility_reason: string | null;
  outcome: string | null;
  attributed_transaction: string | null;
  metadata_json: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export type ChatChannel = "whatsapp" | "messenger" | "instagram";

export interface ChannelAccountStatus {
  channel: ChatChannel | string;
  exists: boolean;
  owner_id: string;
  status: string;
  is_active: boolean;
  display_name: string | null;
  external_id: string | null;
  page_id?: string | null;
  ig_user_id?: string | null;
  phone_number_id?: string | null;
  waba_id?: string | null;
  access_token_configured?: boolean;
  webhook_url?: string | null;
  health?: Record<string, unknown>;
}

export interface ChannelsOverview {
  owner_id?: string;
  channels: ChannelAccountStatus[] | Record<string, ChannelAccountStatus>;
}

export interface ChannelOAuthOption {
  page_id: string;
  page_name?: string | null;
  instagram_business_account_id?: string | null;
  username?: string | null;
}

export interface ChannelOAuthPreviewResult {
  channel: string;
  selection_required: boolean;
  connected?: boolean;
  oauth_session_id?: string | null;
  options?: ChannelOAuthOption[];
  account?: ChannelAccountStatus;
}

export interface ChannelOAuthCallbackPayload {
  code?: string;
  redirect_uri?: string;
  oauth_session_id?: string;
  phone_number_id?: string;
  waba_id?: string;
  page_id?: string;
  page_name?: string | null;
  instagram_business_account_id?: string | null;
  ig_user_id?: string;
}

export interface ChannelTestSendPayload {
  recipient_id?: string;
  phone?: string;
  text: string;
}

type ListParams = {
  channel?: string | null;
  status?: string | null;
  bucket?: string | null;
  search?: string;
  priority?: string | null;
  customer_state?: string | null;
  labels?: string | null;
  human_help?: string | null;
  before?: string | null;
  before_id?: number | null;
  cursor?: string | null;
  active_only?: boolean;
  unanswered?: boolean;
  pending_approval?: boolean;
  delivery_error?: boolean;
  flow_type?: string | null;
  limit?: number;
  offset?: number;
  conversation_id?: number | null;
  trace_id?: string | null;
  provider_key?: string | null;
  tool_name?: string | null;
};

const cleanParams = (params: ListParams = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

export const chatbotV2Service = {
  getRealtimeStreamUrl(): string {
    const baseURL = String(axiosInstanceWhatsAppConfirmation.defaults.baseURL || "").replace(/\/$/, "");
    return `${baseURL}/admin/support/realtime/stream`;
  },

  getRealtimeAuthToken(): string | null {
    if (getLocalOwnerIdOverride()) return "local-spacec-token";
    return decodeTokenService.getToken();
  },

  async getWhatsAppAccount(): Promise<WhatsAppConnectionStatus> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/whatsapp/account");
    return response.data;
  },

  async getChannels(): Promise<ChannelsOverview> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/channels");
    return response.data;
  },

  async getChannelAccount(channel: ChatChannel | string): Promise<ChannelAccountStatus> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/channels/${channel}/account`
    );
    return response.data;
  },

  async channelOAuthPreview(
    channel: ChatChannel | string,
    payload: { code: string; redirect_uri?: string }
  ): Promise<ChannelOAuthPreviewResult> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/channels/${channel}/oauth/preview`,
      payload
    );
    return response.data;
  },

  async channelOAuthCallback(
    channel: ChatChannel | string,
    payload: ChannelOAuthCallbackPayload
  ): Promise<ChannelAccountStatus> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/channels/${channel}/oauth/callback`,
      payload
    );
    return response.data;
  },

  async disconnectChannel(
    channel: ChatChannel | string
  ): Promise<{ status: string; channel: string; owner_id?: string }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/channels/${channel}/disconnect`
    );
    return response.data;
  },

  async diagnoseChannel(channel: ChatChannel | string): Promise<Record<string, unknown>> {
    const response = await axiosInstanceWhatsAppConfirmation.post(`/admin/support/channels/${channel}/diagnose`);
    return response.data;
  },

  async testChannelSend(
    channel: ChatChannel | string,
    payload: ChannelTestSendPayload
  ): Promise<Record<string, unknown>> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/channels/${channel}/test`,
      {
        recipient: payload.recipient_id || payload.phone || "",
        text: payload.text,
      }
    );
    return response.data;
  },

  async saveWhatsAppAccount(payload: ManualWhatsAppAccountPayload): Promise<WhatsAppConnectionStatus> {
    const response = await axiosInstanceWhatsAppConfirmation.put("/admin/support/whatsapp/account", payload);
    return response.data;
  },

  async rotateVerifyToken(): Promise<WhatsAppConnectionStatus> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/whatsapp/verify-token/rotate");
    return response.data;
  },

  async diagnoseWhatsApp(): Promise<Record<string, unknown>> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/whatsapp/diagnose");
    return response.data;
  },

  async syncTemplates(): Promise<{ owner_id: string; synced: number; removed?: number; total?: number }> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/whatsapp/templates/sync");
    return response.data;
  },

  async registerWhatsAppNumber(payload: { pin: string }): Promise<Record<string, unknown>> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/whatsapp/register-number", payload);
    return response.data;
  },

  async subscribeWhatsAppWebhooks(): Promise<Record<string, unknown>> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/whatsapp/webhooks/subscribe");
    return response.data;
  },

  async listTemplates(status?: string): Promise<WhatsAppTemplate[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/whatsapp/templates", {
      params: cleanParams({ status }),
    });
    return response.data;
  },

  async createTemplate(payload: {
    name: string;
    language: string;
    category: string;
    body_text?: string;
    components?: Record<string, unknown>[];
  }): Promise<WhatsAppTemplate> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/whatsapp/templates", payload);
    return response.data;
  },

  // Instala el paquete de plantillas recomendadas de Fluxi (confirmaciones, envíos,
  // recuperación de carrito, seguimientos, Guardián). Se envían a Meta para aprobación.
  async provisionTemplatePackage(): Promise<{ created: string[]; existing: string[]; failed?: string[] }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      "/admin/support/whatsapp/templates/provision-package"
    );
    return response.data;
  },

  async setDefaultTemplates(payload: DefaultTemplatesPayload): Promise<WhatsAppConnectionStatus> {
    const response = await axiosInstanceWhatsAppConfirmation.put(
      "/admin/support/whatsapp/default-templates",
      payload
    );
    return response.data;
  },

  async sendTestMessage(payload: { phone: string; text: string }): Promise<Record<string, unknown>> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/whatsapp/test-send", payload);
    return response.data;
  },

  async getSettings(): Promise<SupportSettings> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/settings");
    return response.data;
  },

  async saveSettings(payload: SupportSettingsPayload): Promise<SupportSettings> {
    const response = await axiosInstanceWhatsAppConfirmation.put("/admin/support/settings", payload);
    return response.data;
  },

  async listSupportMediaResources(): Promise<Record<string, unknown>[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/media-resources");
    return response.data;
  },

  async saveSupportMediaResources(resources: Record<string, unknown>[]): Promise<Record<string, unknown>[]> {
    const response = await axiosInstanceWhatsAppConfirmation.put("/admin/support/media-resources", { resources });
    return response.data;
  },

  async listSupportAutomations(): Promise<SupportAutomation[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/automations");
    return response.data;
  },

  async listSupportAutomationRuns(params: ListParams = {}): Promise<SupportAutomationRun[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/automations/runs", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async saveSupportAutomations(automations: Record<string, unknown>[]): Promise<SupportAutomation[]> {
    const response = await axiosInstanceWhatsAppConfirmation.put("/admin/support/automations", { automations });
    return response.data;
  },

  async listCommerceFlows(flowType?: string): Promise<CommerceFlowApiRow[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/commerce/flows", {
      params: cleanParams({ flow_type: flowType }),
    });
    return response.data;
  },

  async saveCommerceFlows(flowType: string, flows: Record<string, unknown>[]): Promise<CommerceFlowsSaveResult> {
    const response = await axiosInstanceWhatsAppConfirmation.put(
      "/admin/support/commerce/flows",
      { flows },
      { params: cleanParams({ flow_type: flowType }) }
    );
    // Compat: el backend viejo devolvía el array de flows directo; el nuevo devuelve
    // { flows, confirmation_sync } para que el guardado NUNCA sea un no-op silencioso.
    if (Array.isArray(response.data)) {
      return { flows: response.data as CommerceFlowApiRow[], confirmation_sync: null };
    }
    return {
      flows: (response.data?.flows ?? []) as CommerceFlowApiRow[],
      confirmation_sync: (response.data?.confirmation_sync ?? null) as ConfirmationSyncResult | null,
    };
  },

  // Visión una-sola-vez: describe una imagen del embudo para usarla como contexto de la IA.
  async describeFunnelImage(url: string): Promise<{ description: string }> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/funnel-images/describe", { url });
    return response.data;
  },

  async listCommerceProducts(params: { page?: number; size?: number; search?: string } = {}): Promise<unknown> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/commerce/products", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async listCommerceWebsites(): Promise<CommerceWebsiteSummary[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/commerce/websites");
    return response.data;
  },

  // Contexto exacto que el chatbot recibe de un producto en un flujo de toma de órdenes.
  async getFlowProductContext(flowKey: string, productRef: string): Promise<CommerceFlowProductContext> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/commerce/flows/${encodeURIComponent(flowKey)}/product-context`,
      { params: { product_ref: productRef } }
    );
    return response.data;
  },

  async listAIModels(responseLength = "medium"): Promise<AIModel[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/ai-models", {
      params: { response_length: responseLength },
    });
    return response.data;
  },

  async getOverview(since?: string, until?: string): Promise<SupportOverview> {
    const params: Record<string, string> = {};
    if (since) params.since = since;
    if (until) params.until = until;
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/overview", {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  async getOperationsHealth(since?: string, until?: string): Promise<OperationsHealth> {
    const params: Record<string, string> = {};
    if (since) params.since = since;
    if (until) params.until = until;
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/operations-health", {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  async listNotifications(params: ListParams = {}): Promise<SupportNotification[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/notifications", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async getNotificationsSummary(): Promise<NotificationsSummary> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/notifications/summary");
    return response.data;
  },

  async markNotificationRead(notificationId: number): Promise<SupportNotification> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/notifications/${notificationId}/read`
    );
    return response.data;
  },

  async markNotificationsReadByTarget(payload: {
    target_type: string;
    target_id: string;
    type?: string | null;
  }): Promise<{ updated: number }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      "/admin/support/notifications/read-by-target",
      payload
    );
    return response.data;
  },

  async getConfirmationsOverview(): Promise<ConfirmationsOverview> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/confirmations/overview");
    return response.data;
  },

  async listConfirmations(params: ListParams = {}): Promise<SupportConfirmation[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/confirmations", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async getOrderStates(params: ListParams = {}): Promise<StateBoard<SupportConfirmation>> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/states/orders", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async getContactStates(params: ListParams = {}): Promise<StateBoard<ContactStateItem>> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/states/contacts", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async updateOrderState(
    confirmationId: number,
    payload: { status?: string | null; tags?: string[] }
  ): Promise<SupportConfirmation> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/orders/${confirmationId}`,
      payload
    );
    return response.data;
  },

  async listContactOrders(contactId: number, limit = 20): Promise<SupportConfirmation[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/contacts/${contactId}/orders`,
      { params: { limit } }
    );
    return response.data;
  },

  async getRetentionPreview(): Promise<RetentionPreview> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/retention/preview");
    return response.data;
  },

  async runRetentionCleanup(): Promise<RetentionRunResult> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/retention/run");
    return response.data;
  },

  async listIntegrationProviders(): Promise<IntegrationProvider[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/integrations/providers");
    return response.data;
  },

  async listIntegrationConnections(): Promise<IntegrationConnection[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/integrations/connections");
    return response.data;
  },

  async createIntegrationConnection(payload: {
    provider_key: string;
    display_name?: string | null;
    secrets?: Record<string, string | null>;
    metadata_json?: Record<string, unknown>;
  }): Promise<IntegrationConnection> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      "/admin/support/integrations/connections",
      payload
    );
    return response.data;
  },

  async updateIntegrationConnection(
    connectionId: number,
    payload: {
      display_name?: string | null;
      status?: string | null;
      is_active?: boolean | null;
      secrets?: Record<string, string | null>;
      metadata_json?: Record<string, unknown> | null;
    }
  ): Promise<IntegrationConnection> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/integrations/connections/${connectionId}`,
      payload
    );
    return response.data;
  },

  async diagnoseIntegration(connectionId: number): Promise<Record<string, unknown>> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/integrations/connections/${connectionId}/diagnose`
    );
    return response.data;
  },

  async syncIntegrationCatalog(connectionId: number): Promise<Record<string, unknown>> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/integrations/connections/${connectionId}/sync-catalog`
    );
    return response.data;
  },

  async listIntegrationTools(connectionId: number): Promise<IntegrationTool[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/integrations/connections/${connectionId}/tools`
    );
    return response.data;
  },

  async updateIntegrationTool(
    connectionId: number,
    toolName: string,
    payload: { enabled?: boolean; policy?: string | null; metadata_json?: Record<string, unknown> }
  ): Promise<{ tool_name: string; enabled: boolean; policy: string; metadata_json: Record<string, unknown> }> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/integrations/connections/${connectionId}/tools/${toolName}`,
      payload
    );
    return response.data;
  },

  async executeIntegrationTool(
    connectionId: number,
    toolName: string,
    payload: {
      inputs?: Record<string, unknown>;
      actor_type?: string;
      conversation_id?: number | null;
      contact_id?: number | null;
      idempotency_key?: string | null;
      approve?: boolean;
    }
  ): Promise<IntegrationToolExecution> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/integrations/connections/${connectionId}/tools/${toolName}/execute`,
      payload
    );
    return response.data;
  },

  async listIntegrationExecutions(params: ListParams = {}): Promise<IntegrationToolExecution[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/integrations/executions", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async approveIntegrationExecution(executionId: number): Promise<{
    approval: IntegrationToolExecution;
    execution: IntegrationToolExecution;
  }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/integrations/executions/${executionId}/approve`
    );
    return response.data;
  },

  async rejectIntegrationExecution(executionId: number, reason?: string): Promise<IntegrationToolExecution> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/integrations/executions/${executionId}/reject`,
      { reason: reason || null }
    );
    return response.data;
  },

  async reconcileIntegrationExecutions(olderThanMinutes = 10): Promise<{ reconciled: number }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      "/admin/support/integrations/executions/reconcile-stale",
      undefined,
      { params: { older_than_minutes: olderThanMinutes } }
    );
    return response.data;
  },

  async testHotmartClub(
    connectionId: number,
    payload: { api_subdomain: string; sample_email?: string | null }
  ): Promise<Record<string, unknown>> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/integrations/connections/${connectionId}/hotmart/clubs/test`,
      payload
    );
    return response.data;
  },

  async listHotmartClubs(connectionId: number): Promise<HotmartClubConfig[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      "/admin/support/integrations/hotmart/clubs",
      { params: { connection_id: connectionId } }
    );
    return response.data;
  },

  async createHotmartClub(
    connectionId: number,
    payload: {
      display_name: string;
      api_subdomain: string;
      public_url?: string | null;
      club_slug?: string | null;
      product_mapping_ids?: number[];
      metadata_json?: Record<string, unknown>;
    }
  ): Promise<HotmartClubConfig> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      "/admin/support/integrations/hotmart/clubs",
      payload,
      { params: { connection_id: connectionId } }
    );
    return response.data;
  },

  async updateHotmartClub(
    clubId: number,
    payload: Partial<Pick<HotmartClubConfig, "display_name" | "api_subdomain" | "public_url" | "club_slug" | "product_mapping_ids" | "metadata_json">>
  ): Promise<HotmartClubConfig> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/integrations/hotmart/clubs/${clubId}`,
      payload
    );
    return response.data;
  },

  async deleteHotmartClub(clubId: number): Promise<{ deleted: boolean }> {
    const response = await axiosInstanceWhatsAppConfirmation.delete(
      `/admin/support/integrations/hotmart/clubs/${clubId}`
    );
    return response.data;
  },

  async listHotmartProductMappings(connectionId: number): Promise<HotmartProductMapping[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      "/admin/support/integrations/hotmart/product-mappings",
      { params: { connection_id: connectionId } }
    );
    return response.data;
  },

  async updateHotmartProductMapping(
    mappingId: number,
    payload: Partial<Pick<HotmartProductMapping, "aliases" | "club_subdomain" | "club_config_id" | "delivery_type" | "product_type" | "is_active">> & {
      club_public_url?: string | null;
      metadata_json?: Record<string, unknown>;
    }
  ): Promise<HotmartProductMapping> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/integrations/hotmart/product-mappings/${mappingId}`,
      payload
    );
    return response.data;
  },

  async listHotmartAccessCases(params: ListParams = {}): Promise<HotmartAccessCase[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      "/admin/support/integrations/hotmart/access-cases",
      { params: cleanParams(params) }
    );
    return response.data;
  },

  async updateHotmartAccessCase(
    caseId: number,
    payload: Partial<Pick<HotmartAccessCase, "case_status" | "resolution_note" | "recommended_action" | "assigned_to">>
  ): Promise<HotmartAccessCase> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/integrations/hotmart/access-cases/${caseId}`,
      payload
    );
    return response.data;
  },

  async verifyHotmartAccessCase(caseId: number): Promise<HotmartAccessCase> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/integrations/hotmart/access-cases/${caseId}/verify`
    );
    return response.data;
  },

  async listHotmartWebhookEvents(connectionId: number, status?: string): Promise<HotmartWebhookEvent[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      "/admin/support/integrations/hotmart/webhook-events",
      { params: { connection_id: connectionId, status: status || undefined } }
    );
    return response.data;
  },

  async listHotmartCommercialSignals(connectionId: number, status?: string): Promise<HotmartCommercialSignal[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      "/admin/support/integrations/hotmart/commercial-signals",
      { params: { connection_id: connectionId, status: status || undefined } }
    );
    return response.data;
  },

  async retryHotmartWebhookEvent(eventId: number): Promise<{
    event: HotmartWebhookEvent;
    processing: Record<string, unknown>;
  }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/integrations/hotmart/webhook-events/${eventId}/retry`
    );
    return response.data;
  },

  async extractKnowledgeDocument(file: File): Promise<KnowledgeDocumentExtractionResult> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axiosInstanceWhatsAppConfirmation.post(
      "/admin/support/knowledge/extract-document",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  async listKnowledgeSources(params: ListParams = {}): Promise<KnowledgeSource[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/knowledge-sources", {
      params: cleanParams(params),
    });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object") {
      const wrapped = data as { items?: KnowledgeSource[]; sources?: KnowledgeSource[]; data?: KnowledgeSource[] };
      if (Array.isArray(wrapped.items)) return wrapped.items;
      if (Array.isArray(wrapped.sources)) return wrapped.sources;
      if (Array.isArray(wrapped.data)) return wrapped.data;
    }
    return [];
  },

  async createKnowledgeSource(payload: {
    name: string;
    category?: string | null;
    content: string;
    source_type?: string;
    is_active?: boolean;
    metadata_json?: Record<string, unknown>;
  }): Promise<KnowledgeSource> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/knowledge-sources", payload);
    return response.data;
  },

  async updateKnowledgeSource(
    sourceId: number,
    payload: Partial<Pick<KnowledgeSource, "name" | "category" | "content" | "source_type" | "is_active">> & {
      metadata_json?: Record<string, unknown>;
      entries?: LearningEntry[];
    }
  ): Promise<KnowledgeSource> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/knowledge-sources/${sourceId}`,
      payload
    );
    return response.data;
  },

  async deleteKnowledgeSource(sourceId: number): Promise<{ deleted: true }> {
    const response = await axiosInstanceWhatsAppConfirmation.delete(
      `/admin/support/knowledge-sources/${sourceId}`
    );
    return response.data;
  },

  async runTestChat(payload: {
    message: string;
    contact_name?: string | null;
    history?: SupportTestChatHistoryItem[];
  }): Promise<SupportTestChatResponse> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/test-chat", payload);
    return response.data;
  },

  async listContacts(params: ListParams = {}): Promise<SupportContact[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/contacts", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async getContact(contactId: number): Promise<SupportContact> {
    const response = await axiosInstanceWhatsAppConfirmation.get(`/admin/support/contacts/${contactId}`);
    return response.data;
  },

  async getContactPersonView(contactId: number): Promise<PersonView> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/contacts/${contactId}/person-view`
    );
    return response.data;
  },

  // Separa este contacto de su persona unificada: vuelve a ser un cliente independiente.
  async unmergeContact(contactId: number): Promise<ContactUnmergeResult> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/contacts/${contactId}/unmerge`
    );
    return response.data;
  },

  async updateContact(contactId: number, payload: SupportContactUpdate): Promise<SupportContact> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/contacts/${contactId}`,
      payload
    );
    return response.data;
  },

  async addContactNote(
    contactId: number,
    payload: { note: string; category?: string | null; conversation_id?: number | null }
  ): Promise<SupportContactNote> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/contacts/${contactId}/notes`,
      payload
    );
    return response.data;
  },

  async listConversations(params: ListParams = {}): Promise<SupportConversation[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/conversations", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async listConversationsPaged(params: ListParams = {}): Promise<SupportConversationPage> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/conversations/query", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async getConversation(conversationId: number): Promise<SupportConversation> {
    const response = await axiosInstanceWhatsAppConfirmation.get(`/admin/support/conversations/${conversationId}`);
    return response.data;
  },

  async updateConversation(
    conversationId: number,
    payload: SupportConversationUpdate
  ): Promise<SupportConversation> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/conversations/${conversationId}`,
      payload
    );
    return response.data;
  },

  async toggleConversationAI(conversationId: number, aiEnabled: boolean): Promise<SupportConversation> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/conversations/${conversationId}/ai-toggle`,
      { ai_enabled: aiEnabled }
    );
    return response.data;
  },

  async resolveConversation(conversationId: number): Promise<SupportConversation> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/conversations/${conversationId}/resolve`
    );
    return response.data;
  },

  async reopenConversation(conversationId: number): Promise<SupportConversation> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/conversations/${conversationId}/reopen`
    );
    return response.data;
  },

  async createPrivateNote(conversationId: number, content: string): Promise<SupportMessage> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/conversations/${conversationId}/private-notes`,
      { content }
    );
    return response.data;
  },

  async resetConversationForDebug(conversationId: number): Promise<{
    status: string;
    conversation_id: number;
    contact_id?: number | null;
    backup_contact_id?: number | null;
    backup_suffix?: string | null;
    backup_phone?: string | null;
    orders_reassigned: number;
    chat_confirmation_orders_reassigned?: number;
    ecommerce_orders_reassigned?: number;
    ecommerce_reassign_error?: string | null;
    ecommerce_reassign_result?: Record<string, unknown> | null;
    conversations_archived: number;
    messages_reassigned: number;
    order_drafts_reassigned: number;
    ai_attempts_reassigned?: number;
  }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/conversations/${conversationId}/debug-reset`
    );
    return response.data;
  },

  async fetchInboundMediaUrl(messageId: number): Promise<{
    url: string;
    mime_type?: string | null;
    media_type?: string | null;
    filename?: string | null;
    size_bytes?: number | null;
    expires_in_seconds?: number;
  }> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/messages/${messageId}/inbound-media-url`
    );
    return response.data;
  },

  async fetchInboundWhatsAppMediaBlob(mediaId: string): Promise<Blob> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/whatsapp-inbound-media/${encodeURIComponent(mediaId)}`,
      { responseType: "blob" }
    );
    return response.data;
  },

  async retryConversationAI(
    conversationId: number,
    payload: { instruction?: string | null } = {}
  ): Promise<{ sent: boolean }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/conversations/${conversationId}/retry-ai`,
      payload
    );
    return response.data;
  },

  async cancelConversationAI(conversationId: number): Promise<{ cancelled?: boolean }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/conversations/${conversationId}/cancel-ai`
    );
    return response.data;
  },

  async getConversationReplyApproval(
    conversationId: number
  ): Promise<{ active: ReplyApproval | null; recent: ReplyApproval[] }> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/conversations/${conversationId}/reply-approval`
    );
    return response.data;
  },

  async approveReplyApproval(
    id: number,
    payload: { version?: number; force?: boolean } = {}
  ): Promise<ReplyApproval> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/reply-approvals/${id}/approve`,
      payload
    );
    return response.data;
  },

  async getReplyApprovalVersionVoice(id: number, version: number): Promise<{ url: string }> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/reply-approvals/${id}/versions/${version}/voice`
    );
    return response.data;
  },

  async sendReplyApprovalFeedback(id: number, text: string): Promise<{ status: string }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/reply-approvals/${id}/feedback`,
      { text }
    );
    return response.data;
  },

  async rejectReplyApproval(
    id: number,
    reason?: string
  ): Promise<{ status: string; executed_commerce: unknown[]; reversal: "started" | "none" }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/reply-approvals/${id}/reject`,
      { reason: reason || null }
    );
    return response.data;
  },

  async retryInboundMediaExtraction(messageId: number): Promise<{
    status: string;
    message_id?: number;
    error?: string;
  }> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/messages/${messageId}/retry-media-extraction`
    );
    return response.data;
  },

  async listMessages(conversationId: number, params: ListParams = {}): Promise<SupportMessage[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/conversations/${conversationId}/messages`,
      { params: cleanParams(params) }
    );
    return response.data;
  },

  async getMessageDebugMetadata(messageId: number): Promise<{
    message_id: number;
    conversation_id: number;
    metadata_json: Record<string, unknown>;
    slim_metadata_json: Record<string, unknown>;
    metadata_bytes: number;
  }> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/messages/${messageId}/debug-metadata`
    );
    return response.data;
  },

  async sendConversationMessage(
    conversationId: number,
    payload: { content: string; disable_ai?: boolean; client_message_id: string }
  ): Promise<SupportMessage> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/conversations/${conversationId}/messages`,
      payload
    );
    return response.data;
  },

  async sendConversationTemplate(
    conversationId: number,
    payload: { template_name: string; language?: string | null; variables?: string[]; client_message_id: string }
  ): Promise<SupportMessage> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/conversations/${conversationId}/templates`,
      payload
    );
    return response.data;
  },

  async sendConversationMedia(
    conversationId: number,
    payload: { file: File; kind: "image" | "video" | "voice"; caption?: string }
  ): Promise<SupportMessage> {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("kind", payload.kind);
    if (payload.caption) formData.append("caption", payload.caption);
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/conversations/${conversationId}/media`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  async listEvents(params: ListParams = {}): Promise<ChatbotEvent[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/events", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async listDeliveryLogs(params: ListParams = {}): Promise<ChatbotDeliveryLog[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/delivery-logs", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async listAIGenerationAttempts(params: ListParams = {}): Promise<SupportAIGenerationAttempt[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/ai-generation-attempts", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async listInboundEvents(params: ListParams = {}): Promise<WhatsAppInboundEvent[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/inbound-events", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async retryInboundEvent(eventId: number): Promise<InboundRetryResult> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/inbound-events/${eventId}/retry`
    );
    return response.data;
  },

  async getGuardianSettings(): Promise<GuardianSettings> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/guardian-settings");
    return response.data;
  },

  async saveGuardianSettings(payload: GuardianSettingsRequest): Promise<GuardianSettings> {
    const response = await axiosInstanceWhatsAppConfirmation.put("/admin/support/guardian-settings", payload);
    return response.data;
  },

  async listGuardianRuns(): Promise<GuardianRun[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/guardian-runs");
    return response.data;
  },

  async getGuardianState(): Promise<GuardianState> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/guardian-state");
    return response.data;
  },

  async listGuardianMemory(): Promise<GuardianMemoryItem[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/guardian-memory");
    return response.data;
  },

  async listGuardianTimeline(params: { limit?: number; before?: string } = {}): Promise<GuardianTimelineItem[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/guardian-timeline", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async createGuardianMemory(payload: {
    title?: string | null;
    content: string;
    status?: string;
    tags?: string[];
  }): Promise<GuardianMemoryItem> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/guardian-memory", payload);
    return response.data;
  },

  async updateGuardianMemory(
    memoryId: string,
    payload: Partial<Pick<GuardianMemoryItem, "title" | "content" | "status" | "tags">>
  ): Promise<GuardianMemoryItem> {
    const response = await axiosInstanceWhatsAppConfirmation.patch(
      `/admin/support/guardian-memory/${memoryId}`,
      payload
    );
    return response.data;
  },

  async listGuardianProposals(params: ListParams = {}): Promise<GuardianProposal[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/guardian-proposals", {
      params: cleanParams(params),
    });
    return response.data;
  },

  async listGuardianConversations(): Promise<GuardianConversation[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get("/admin/support/guardian-conversations");
    return response.data;
  },

  async createGuardianConversation(payload: { title?: string | null } = {}): Promise<GuardianConversation> {
    const response = await axiosInstanceWhatsAppConfirmation.post("/admin/support/guardian-conversations", payload);
    return response.data;
  },

  async listGuardianMessages(conversationId: string): Promise<GuardianChatMessage[]> {
    const response = await axiosInstanceWhatsAppConfirmation.get(
      `/admin/support/guardian-conversations/${conversationId}/messages`
    );
    return response.data;
  },

  async sendGuardianMessage(conversationId: string, content: string): Promise<GuardianConversation> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/guardian-conversations/${conversationId}/messages`,
      { content }
    );
    return response.data;
  },

  async approveGuardianProposal(
    proposalId: string,
    note?: string | null,
    userInput?: string | null
  ): Promise<GuardianProposal> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/guardian-proposals/${proposalId}/approve`,
      { note: note || null, user_input: userInput?.trim() ? userInput.trim() : null }
    );
    return response.data;
  },

  async rejectGuardianProposal(proposalId: string, note?: string | null): Promise<GuardianProposal> {
    const response = await axiosInstanceWhatsAppConfirmation.post(
      `/admin/support/guardian-proposals/${proposalId}/reject`,
      { note: note || null }
    );
    return response.data;
  },
};
